import SEO from '../SEO';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-green-50">
      <SEO
        title="Privacy Policy | MetaFit Wellness"
        description="Read the official Privacy Policy of MetaFit Wellness (operated by Romeja Wellness LLP) explaining how we protect, collect, and handle your data."
        keywords="privacy policy, data protection, user privacy, metafit terms, security"
        url="https://www.metafitwellness.com/policy/privacy-policy"
        canonical="https://www.metafitwellness.com/policy/privacy-policy"
      />
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-teal-100">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <h1 className="text-4xl font-bold text-gray-800 text-center">Privacy Policy</h1>
          <div className="w-24 h-1 bg-gradient-to-r from-teal-400 to-teal-600 mx-auto mt-4 rounded-full"></div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 border border-teal-100">
          
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              This Privacy Policy explains how Metafit Wellness (operated by Romeja Wellness LLP) ("we," "us," or "our") collects, uses, and discloses information gathered from users ("you" or "user") of our platform and services. We are dedicated to safeguarding your privacy and ensuring that your personal information is handled with the utmost care.
            </p>
          </section>

          {/* Information Collection */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Information Collection</h2>
            <div className="space-y-4">
              <p className="text-gray-600 leading-relaxed">
                Personal Information: We may collect personal information such as your name, email address, billing details, and other information necessary for providing and enhancing our services. This information helps us deliver a more efficient and customized user experience.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Usage Data: In addition to personal details, we may collect usage data, such as your interactions with our platform and services. This data helps us understand how our services are used and identify areas for improvement.
              </p>
              <p className="text-gray-600 leading-relaxed">
                You are responsible for maintaining the confidentiality of your account and password.
              </p>
            </div>
          </section>

          {/* Use of Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Use of Information</h2>
            <div className="space-y-4">
              <p className="text-gray-600 leading-relaxed">
                Service Provision: We use the information we collect to provide, maintain, and improve our services. This includes communicating with you about updates, changes, and relevant information that may enhance your experience.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Personalization: We may use your information to personalize your experience by tailoring content, features, and recommendations to better suit your preferences and needs.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Marketing: With your consent, we may use your contact information to send you promotional materials and updates about our services. You can opt out of these communications at any time.
              </p>
            </div>
          </section>

          {/* Data Sharing and Disclosure */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Data Sharing and Disclosure</h2>
            <div className="space-y-4">
              <p className="text-gray-600 leading-relaxed">
                Third-Party Service Providers: We may share your information with third-party service providers who assist us in operating our platform and delivering our services. These providers are contractually obligated to maintain the confidentiality of your information and use it solely for the purposes for which it was shared.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Compliance with Laws: We may disclose your information if required to do so by law or to protect our rights and interests. This includes responding to legal requests, enforcing our terms, and ensuring compliance with applicable regulations.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Business Transfers: In the event of a merger, acquisition, or sale of our business, your information may be transferred as part of the transaction. We will ensure that your information is protected and used in accordance with this Privacy Policy.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We will retain your information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy. Once your information is no longer needed, we will take appropriate measures to securely dispose of it.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Your Rights</h2>
            <div className="space-y-4">
              <p className="text-gray-600 leading-relaxed">
                Access and Control: You have the right to access, correct, update, or delete your personal information. You can also request restrictions on how we use certain information about you.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Data Portability: If applicable, you may request a copy of your personal information in a structured, commonly used format.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Objections: If you believe that our processing of your personal information violates applicable laws, you have the right to lodge a complaint with a relevant data protection authority.
              </p>
            </div>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Children's Privacy</h2>
            <div className="space-y-4">
              <p className="text-gray-600 leading-relaxed">
                Our services are not designed for individuals under the age of 18. We do not knowingly collect personal information from children under 13. If we become aware that we have inadvertently collected such information, we will promptly delete it from our records.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Data Portability: If applicable, you may request a copy of your personal information in a structured, commonly used format.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Objections: If you believe that our processing of your personal information violates applicable laws, you have the right to lodge a complaint with a relevant data protection authority.
              </p>
            </div>
          </section>

          {/* Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Security</h2>
            <p className="text-gray-600 leading-relaxed">
              We implement reasonable security measures to protect your information from unauthorized access, alteration, or disclosure. While we strive to safeguard your data, please be aware that no method of transmission over the internet or electronic storage is completely secure.
            </p>
          </section>

          {/* Changes to This Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We reserve the right to update or modify our Privacy Policy at any time. Any changes will be communicated by posting the revised Privacy Policy on our website. We encourage you to review this policy periodically to stay informed about how we protect your information.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-gradient-to-r from-teal-50 to-green-50 rounded-lg p-6 border border-teal-200">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have any questions or concerns about this Privacy Policy, please contact us at hello@metafitwellness.com. We are committed to addressing your inquiries and ensuring your privacy is respected.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;